import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService, TokenService } from '../Services/auth';
import { AlertService } from '../Services/notifications/alert.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private router: Router,
    private alertService: AlertService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor for certain URLs (like login, public APIs, assets)
    if (this.shouldSkipInterceptor(req.url)) {
      return next.handle(req);
    }

    // Add auth header if token exists
    const authReq = this.addAuthHeader(req);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private shouldSkipInterceptor(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      const path = parsed.pathname || '';
      // skip exact '/auth' path used for login
      if (path === '/api/v1/auth') return true;
      if (path === '/register' || path === '/register/') return true;
      if (path.startsWith('/assets/')) return true;
    } catch (e) {
      if (url.endsWith('/auth') || url.endsWith('/auth/')) return true;
      if (url.endsWith('/register') || url.endsWith('/register/')) return true;
      if (url.includes('/assets/')) return true;
    }
    return false;
  }

  private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    const authHeader = this.tokenService.getAuthorizationHeader();
    // console.log('AuthInterceptor - Adding Authorization Header:', authHeader);
    if (authHeader) {
      return request.clone({
        setHeaders: {
          Authorization: authHeader,
          'Content-Type': 'application/json'
        }
      });
    }

    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json'
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.handleLogout();
    return EMPTY;
  }

  private handleLogout(): void {
    try {
      // Session is already expired on the backend (401 received), so skip the
      // backend logout API call and just clear the local session.
      this.authService.logoutLocal(true, 'Session expired. You have been logged out.');
    } catch (e) { /* ignore */ }
  }
}